import React, { useCallback, useState } from "react"
import { useMount } from "react-use"
import { useRouter } from "next/router"
import axios from "axios"
import routes from "routes"
import Link from "next/link"
import { GetServerSideProps } from "next"

import { Info, SlideJsonResponsive } from "types"
import { WorkItem } from "../../models/WorkItem/WorkItem"
import TieupImg from "src/components/common/TieupImg"
import { useCreatorSearch } from "src/components/hooks/useCreatorSearch"
import { useJudgeDevice } from "src/components/hooks/useJudgeDevice"
import DefaultLayout from "src/components/common/layouts/DefaultLayout"
import HeroSlideShow from "src/components/pages/creator/top/HeroSlideShow"
import SearchForm from "src/components/pages/creator/top/SearchForm"
import WorkCard from "src/components/pages/creator/top/WorkCard"
import InfoList from "src/components/pages/creator/top/InfoList"
import HTMLHead from "src/components/utils/HTMLHead"
import { useUser } from "src/utils/auth/useUser"
import URLUtils from "src/utils/URLUtils"
import InfoUtil from "src/utils/InfoUtil"
import { http } from "src/services/http"

type Props = {
  heroSlides: SlideJsonResponsive[]
  workList: WorkItem[]
  infoSSR: string
}

/** server-side */
export const getServerSideProps: GetServerSideProps = async () => {
  /** カルーセル用スライド */
  let heroSlides: SlideJsonResponsive[] = []
  try {
    const res = await axios.get(
      `${URLUtils.getStaticPage("parts/creator_slide.json")}`,
    )
    heroSlides = res.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(">>>>>>>>>> スライドショーのエラー >>>>>>>>>>")
    // eslint-disable-next-line no-console
    console.log(error)
  }

  /** 最近たいあっぷした作品 */
  let workList: WorkItem[] = []
  try {
    const res = await http.get(`/v1/tieup/list`)
    workList = res.data.results
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
  }

  /** お知らせ */
  let infoSSR = ""
  try {
    const res = await axios.get(`${URLUtils.getHelpPage("info/index.html")}`)
    infoSSR = res.data
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(">>>>>>>>>> お知らせ一覧のエラー >>>>>>>>>>")
    // eslint-disable-next-line no-console
    console.log(error)
  }

  return { props: { heroSlides, workList, infoSSR } }
}

/** client-side */
const CreatorTop: React.FC<Props> = ({ heroSlides, workList, infoSSR }) => {
  const { setSearchType, searchType, updateSearchText, urlParams } =
    useCreatorSearch()

  const router = useRouter()

  const { isMember } = useUser()

  const [info, setInfo] = useState<Info>([])

  const { isLarge } = useJudgeDevice("md")

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()

      router.push({
        pathname: `/creator/${searchType}`,
        query: {
          q: urlParams.q ? urlParams.q : null,
        },
      })
    },
    [router, searchType, urlParams.q],
  )

  useMount(() => {
    setInfo(InfoUtil.parseHtml(infoSSR))
  })

  return (
    <DefaultLayout isCreatorMyPage>
      <HTMLHead path="creator" />
      <main>
        {heroSlides.length > 0 ? <HeroSlideShow slides={heroSlides} /> : null}
        {isMember ? (
          <div className="creatorIndex-loggedIn">
            <section className="creatorIndex-loggedIn-section creatorIndex-loggedIn-post">
              <h2 className="creatorIndex-sectionHeading">作品を投稿する</h2>

              <div className="creatorIndex-loggedIn-post-buttonWrapper">
                <div className="creatorIndex-loggedIn-post-button">
                  <Link href={routes.creatorMyPageNovelsCreateNovel()}>
                    <a className="c-button-secondary -full">
                      <i className="fas fa-book creatorIndex-loggedIn-post-button-icon"></i>
                      <span className="creatorIndex-loggedIn-post-button-text">
                        新規小説投稿
                      </span>
                    </a>
                  </Link>
                </div>

                <div className="creatorIndex-loggedIn-post-button">
                  <Link href={routes.creatorMyPageIllustrationNew()}>
                    <a className="c-button-secondary -full">
                      <i className="fas fa-image creatorIndex-loggedIn-post-button-icon"></i>
                      <span className="creatorIndex-loggedIn-post-button-text">
                        新規イラスト投稿
                      </span>
                    </a>
                  </Link>
                </div>
              </div>

              <div className="creatorIndex-loggedIn-post-bnr">
                <a
                  href={URLUtils.getHelpPage("guide/index.html")}
                  target="_blank"
                  rel="noreferrer"
                  className="creatorIndex-loggedIn-post-bnr-link"
                >
                  <TieupImg
                    src="/img/bnr_guide.png"
                    alt="はじめての「たいあっぷ」ガイド"
                  />
                </a>
              </div>
            </section>

            <section className="creatorIndex-loggedIn-section creatorIndex-loggedIn-info">
              <h2 className="creatorIndex-sectionHeading">
                運営からのお知らせ
              </h2>

              {isLarge && (
                <a
                  className="c-button-secondary creatorIndex-loggedIn-info-button"
                  href={`${URLUtils.getHelpPage("info/index.html")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  お知らせ一覧
                </a>
              )}
              <InfoList info={info} />
            </section>

            <section className="creatorIndex-loggedIn-section creatorIndex-loggedIn-search">
              <h2 className="creatorIndex-sectionHeading">パートナーを探す</h2>
              <div className="creatorIndex-loggedIn-searchForm">
                <SearchForm
                  onSubmit={onSubmit}
                  searchType={searchType}
                  setSearchType={setSearchType}
                  updateSearchText={updateSearchText}
                  searchText={urlParams.q}
                  type="type02"
                />
              </div>
            </section>
          </div>
        ) : (
          <>
            <section className="l-single creatorIndex-invitation">
              <h2 className="creatorIndex-invitation-title">
                たいあっぷをはじめよう！
              </h2>
              <div>
                <p className="creatorIndex-invitation-paragraph">
                  「たいあっぷ」は、小説とイラストを組み合わせ、公開、閲覧できるサービスです。
                  <br />
                  二人だから作れる「たいあっぷノベル」はイラストと小説をより魅力的なものに変えます。
                </p>
                <p className="creatorIndex-invitation-paragraph">
                  イラストを「もっと心に残るイラスト」へ
                  <br />
                  小説を「もっと胸に響く小説」へ
                  <br />
                  クリエイターを「もっと夢ある職業」へ
                </p>
                <p className="creatorIndex-invitation-paragraph">
                  その答えが「たいあっぷ」です。
                </p>
              </div>

              <div className="creatorIndex-invitation-link">
                <Link href={routes.authSignUp()}>
                  <a className="creatorIndex-invitation-link-button">
                    新規会員登録
                  </a>
                </Link>
              </div>
            </section>
            <section className="creatorIndex-search">
              <h2 className="creatorIndex-sectionHeading">投稿作品を探す</h2>
              <div className="creatorIndex-searchForm">
                <SearchForm
                  onSubmit={onSubmit}
                  searchType={searchType}
                  setSearchType={setSearchType}
                  updateSearchText={updateSearchText}
                  searchText={urlParams.q}
                  type="type01"
                />
              </div>
            </section>
          </>
        )}

        <section className="creatorIndex-latestWorks">
          <h2 className="creatorIndex-sectionHeading -wide">
            最近たいあっぷした作品
          </h2>
          <ul className="creatorIndex-latestWorks-list">
            {workList &&
              workList.map((w: WorkItem) => (
                <li key={w.novel.id}>
                  <WorkCard
                    title={w.novel.title}
                    catchCopy={w.novel.catch_copy}
                    thumbnailUrl={
                      w.novel.cover_template_image || "/img/cover.png"
                    }
                    link={routes.seriesDetail(w.novel.id.toString())}
                  />
                </li>
              ))}
          </ul>
        </section>
      </main>
    </DefaultLayout>
  )
}

export default CreatorTop
